/**
 * streamPlayerSdk — idempotent loaders for the Twitch + YouTube player SDKs.
 *
 * Bare iframes give us no play-state; the SDKs do (play/pause/ended + mute),
 * which is what the accessible watch-time model needs to know a human is
 * actually watching. Each loader injects the provider script at most once and
 * resolves when the global (`window.Twitch` / `window.YT`) is ready, so many
 * players mounting concurrently share a single script load.
 */

/* Minimal shapes for the bits of each SDK we use — avoids pulling in provider type packages. */
export interface TwitchPlayerInstance {
    addEventListener(event: string, cb: () => void): void;
    getMuted(): boolean;
    getEnded?(): boolean;
    pause?(): void;
}
export interface TwitchPlayerCtor {
    new (elementId: string, options: Record<string, unknown>): TwitchPlayerInstance;
    PLAY: string;
    PAUSE: string;
    ENDED: string;
    ONLINE: string;
    OFFLINE: string;
    READY: string;
}
interface TwitchGlobal {
    Player: TwitchPlayerCtor
}

export interface YouTubePlayerInstance {
    isMuted?(): boolean;
    getPlayerState?(): number;
    destroy?(): void;
}
interface YouTubeGlobal {
    Player: new (elementId: string, options: Record<string, unknown>) => YouTubePlayerInstance;
    PlayerState: { PLAYING: number; PAUSED: number; ENDED: number; BUFFERING: number; CUED: number; UNSTARTED: number };
}

declare global {
    interface Window {
        Twitch?: TwitchGlobal;
        YT?: YouTubeGlobal;
        onYouTubeIframeAPIReady?: () => void;
    }
}

const TWITCH_PLAYER_SDK_SRC = "https://player.twitch.tv/js/embed/v1.js";
const YOUTUBE_IFRAME_API_SRC = "https://www.youtube.com/iframe_api";

let twitchPromise: Promise<TwitchGlobal> | null = null;
let youtubePromise: Promise<YouTubeGlobal> | null = null;

/** Load the Twitch player SDK (player-only, no chat). Resolves with window.Twitch. */
export function loadTwitchPlayerSdk(): Promise<TwitchGlobal> {
    if (window.Twitch?.Player) {
        return Promise.resolve(window.Twitch);
    }
    if (twitchPromise) {
        return twitchPromise;
    }
    twitchPromise = new Promise<TwitchGlobal>((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(`script[src="${TWITCH_PLAYER_SDK_SRC}"]`);
        const onLoad = () => {
            if (window.Twitch?.Player) resolve(window.Twitch);
            else reject(new Error("Twitch SDK loaded but window.Twitch.Player missing"));
        };
        if (existing) {
            existing.addEventListener("load", onLoad);
            existing.addEventListener("error", () => reject(new Error("Twitch SDK failed to load")));
            if (window.Twitch?.Player) onLoad();
            return;
        }
        const script = document.createElement("script");
        script.src = TWITCH_PLAYER_SDK_SRC;
        script.async = true;
        script.addEventListener("load", onLoad);
        script.addEventListener("error", () => reject(new Error("Twitch SDK failed to load")));
        document.head.appendChild(script);
    });
    return twitchPromise;
}

/**
 * Load the YouTube IFrame API. The API signals readiness via the global
 * window.onYouTubeIframeAPIReady callback, so we chain any pre-existing handler
 * rather than clobbering it.
 */
export function loadYouTubeIframeApi(): Promise<YouTubeGlobal> {
    if (window.YT?.Player) {
        return Promise.resolve(window.YT);
    }
    if (youtubePromise) {
        return youtubePromise;
    }
    youtubePromise = new Promise<YouTubeGlobal>((resolve, reject) => {
        const priorReady = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            priorReady?.();
            if (window.YT?.Player) resolve(window.YT);
            else reject(new Error("YouTube API ready but window.YT.Player missing"));
        };
        if (!document.querySelector<HTMLScriptElement>(`script[src="${YOUTUBE_IFRAME_API_SRC}"]`)) {
            const script = document.createElement("script");
            script.src = YOUTUBE_IFRAME_API_SRC;
            script.async = true;
            script.addEventListener("error", () => reject(new Error("YouTube IFrame API failed to load")));
            document.head.appendChild(script);
        }
    });
    return youtubePromise;
}
